import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGetWarehouseAddress } from '@steelbuy/data-access';
import { formatAddressLabel } from '@steelbuy/form';
import {
    QuoteEntryDetail,
    RFQDetailsHeading,
    RFQQuoteEntryMaterial,
    RFQStatus,
    Literal,
    getDimensionUnit,
    getTradeUnit,
} from '@steelbuy/ts-shared';
import { DownloadButton } from '@steelbuy/ui-domain';
import { Card } from '@steelbuy/ui-primitive';
import { entries, isRequestQuotePath } from '@steelbuy/util';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';

import '../RFQCommonStyles.scss';
import './CardContentRFQDetails.scss';

export const TableRow = ({
    data,
    row,
    showDeliveryAddress,
}: {
    data: RFQQuoteEntryMaterial;
    row: RFQDetailsHeading;
    showDeliveryAddress: boolean;
}) => {
    const { t } = useTranslation('domainModel');
    const { data: warehouseAddresses = [] } = useGetWarehouseAddress();
    return (
        <tr className={classNames('rfq-row')}>
            {entries(row).map(([key, { literalPath, valueMapper }]) => {
                let value: string | number | undefined = '';
                if (data) {
                    if (valueMapper) {
                        value = valueMapper(data);
                    } else if (data[key]) {
                        if (literalPath) {
                            value = t(`domainModel:${String(literalPath)}.${data[key]}` as Literal);
                        } else {
                            value = data[key];
                        }
                    }
                }
                return (
                    <td key={key}>
                        <span>{value}</span>
                    </td>
                );
            })}
            {showDeliveryAddress && (
                <td className="rfq-row__delivery-address-cell">
                    <span>
                        {data?.deliveryAddress
                            ? formatAddressLabel(
                                  warehouseAddresses.find((a) => a.id === data.deliveryAddress),
                                  t as TFunction
                              )
                            : ''}
                    </span>
                </td>
            )}
        </tr>
    );
};

type CardContentRFQDetailsProps = {
    quoteEntries: QuoteEntryDetail[];
    refNumber?: string;
    status?: RFQStatus;
    id?: string;
};

export const CardContentRFQDetails = ({ quoteEntries, refNumber, status, id }: CardContentRFQDetailsProps) => {
    const { t } = useTranslation(['translation', 'domainModel']);
    const { pathname } = useLocation();
    const isRequestQuote = isRequestQuotePath(pathname);
    return (
        <div className="card-content-rfq-details">
            <Card isClickable={false}>
                <div className="card-content-rfq-details__title">
                    {t('translation:application.createRFQ.rfqDetails')}
                    {refNumber && (
                        <DownloadButton
                            downloadUrl={`/api/universal/documents/quote/${id}/download`}
                            label={t('translation:application.createRFQ.downloadRfq')}
                            disabled={status === RFQStatus.WITHDRAWN || status === RFQStatus.EXPIRED}
                            data-testid="download-rfq-button"
                            fileName={`SteelBuy-RFQ[${refNumber}].xls`}
                        />
                    )}
                </div>
                {refNumber && (
                    <div className="card-content-rfq-details__reference">
                        <div className="card-content-rfq-details__reference__label">
                            {t('translation:application.createRFQ.rfqReferenceNumber')}
                        </div>
                        {refNumber}
                    </div>
                )}
                {quoteEntries.map(({ materialType, product, fieldHeadings, materials, group: { label, value } }) => (
                    <div className="card-content-rfq-details__content" key={`rfq-details-group-${value}`}>
                        <RFQTeaser material={{ materialType, product, [label]: value }} />
                        <div className="rfq-table">
                            <table className="rfq-table__table">
                                <thead>
                                    <tr className="rfq-table__label">
                                        {entries(fieldHeadings).map(
                                            ([key, { measurementLiteral, measurementType }]) => {
                                                let measurementText = '';
                                                if (measurementType !== undefined) {
                                                    const literal =
                                                        measurementType === 'dimension'
                                                            ? `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                            : (`domainModel:material.tradeUnit.value.${getTradeUnit({
                                                                  materialType,
                                                                  product,
                                                              })}.label` as Literal);
                                                    measurementText = ` (${t(literal)})`;
                                                } else if (measurementLiteral) {
                                                    const literalText = t(
                                                        `domainModel:${measurementLiteral}` as Literal
                                                    );
                                                    measurementText = ` (${literalText})`;
                                                }
                                                return (
                                                    <th key={key}>
                                                        {t(
                                                            `translation:application.rfqHeadingLabels.${key}` as Literal
                                                        )}
                                                        {measurementText ? <span>{measurementText}</span> : null}
                                                    </th>
                                                );
                                            }
                                        )}
                                        {isRequestQuote && (
                                            <th>{t('translation:application.rfqHeadingLabels.deliveryAddress')}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {materials.map((data, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <TableRow
                                            data={data}
                                            row={fieldHeadings}
                                            key={data.id ? `${data.id}` : `rfq-details-group-${value}-${index}`}
                                            showDeliveryAddress={isRequestQuote}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </Card>
        </div>
    );
};
